import React, { FunctionComponent } from 'react';

import CardBox from '../CardBox';
import DownloadTicket from '../DownloadTicket';
import {
  Order,
  Product as ProductInterface,
  Shipping,
  Service,
  Delivery,
  OrderStatus,
} from '../../requests/caipirinha-api/interfaces/order.interface';
import { Product } from '../../pages/Order/styles';
import { toBRL } from '../../utils/money.util';
import { normalizeText } from '../../utils/text.util';
import { Image } from './styles';
import Link from '../Link';

interface Props {
  order?: Order;
  products?: ProductInterface[];
  services?: Service[];
  shipping?: Shipping;
  delivery?: Delivery;
  purposePurchase?: string;
}

interface LinkProps {
  service: Service;
  orderId: string;
  'data-cy': string;
}

const OrderDetails: FunctionComponent<Props> = ({
  order,
  products,
  shipping,
  services,
  delivery,
  purposePurchase,
}) => {
  const LinkToAuthorizationTerm = ({ service, orderId, 'data-cy': dataCy }: LinkProps) => {
    return (
      <Link
        title='Ver termo de autorização'
        to={{
          pathname: `/${orderId}/servico/${service.id}/termo-de-autorizacao`,
          state: { order, description: service.description },
        }}
        data-cy={dataCy}/>
    );
  };

  return (
    <CardBox>
      {products && products.length > 0 && (
        <>
          <h3>Produtos</h3>
          {products.map(product => (
            <Product key={product.id} data-cy='product-container'>
              <div className="item">
                <Image
                  src={product.image}
                  alt={product.description}
                  title={product.description}
                />
                <p>
                  {product.description} ({product.quantity})
                </p>
              </div>
              {product.seller && (
                <span className="seller" data-cy='seller-name'>
                  Vendido por{' '}
                  <strong>
                    {product.seller.description
                      ? product.seller.description
                      : product.seller.id}
                  </strong>
                </span>
              )}
              <span className="price" data-cy='product-price'>
                <strong>{toBRL(product.value)}</strong>
              </span>
              {product.services &&
                product.services.length > 0 &&
                product.services.map(service => (
                  <div key={service.id} data-cy="service-container">
                    <span className="details">
                      <p>
                        {normalizeText(service.description)} ({service.quantity})
                      </p>
                      <strong>{toBRL(service.value)}</strong>
                    </span>
                    {service.ticket && order?.status === OrderStatus.PAID && (
                      <DownloadTicket
                        orderId={order.id}
                        docType="PDF"
                        serviceId={service.id}
                      />
                    )}
                    { order && order.status !== OrderStatus.PAID && (
                      <LinkToAuthorizationTerm
                        service={service}
                        orderId={order.id}
                        data-cy='product-service-authorization-term' />
                    )}
                  </div>
                ))}
              {product.assembly && (
                <span className="details" key={product.assembly.id} data-cy="assembly-container">
                  <p>Montagem ({product.assembly.quantity})</p>
                  <strong>{toBRL(product.assembly.value)}</strong>
                </span>
              )}
              <hr />
            </Product>
          ))}
        </>
      )}

      {services && services.length > 0 && (
        <>
          <h3>Serviços</h3>
          {services.map(service => (
            <div key={service.id} data-cy='services-container'>
              <span className="details">
                <p>
                  {normalizeText(service.description)} ({service.quantity})
                </p>
                <strong>{toBRL(service.value)}</strong>
              </span>
              {service.ticket && order?.status === OrderStatus.PAID && (
                <DownloadTicket
                  orderId={order.id}
                  docType="PDF"
                  serviceId={service.id}
                />
              )}
              { order && order.status !== OrderStatus.PAID && (
                <LinkToAuthorizationTerm
                  service={service}
                  orderId={order.id}
                  data-cy='service-authorization-term' />
              )}
            </div>
          ))}
          <hr />
        </>
      )}

      {delivery && delivery.type !== 'pickupstore' && shipping && (
        <span className="details" data-cy="delivery-container">
          <h3>Frete</h3>
          <strong>
            {shipping.value === 0 ? 'Grátis' : `${toBRL(shipping.value)}`}
          </strong>
        </span>
      )}

      {delivery && delivery.type === 'pickupstore' && (
        <span className="details" data-cy='delivery-pickupstore-container'>
          <h3>Retirar na Loja</h3>
          <p className="new-line">
            Você será notificado quando o produto estiver disponível para
            retirada.
          </p>
        </span>
      )}

      {purposePurchase && (
        <>
          <hr />
          <span className="details" data-cy="purpose-purchase">
            <p>Finalidade da compra</p>
            <strong>{purposePurchase}</strong>
          </span>
        </>
      )}
    </CardBox>
  );
};

export default OrderDetails;
